import React, {useEffect, useState} from "react";
import Fullscreen from "@sharedComponents/Fullscreen";
import {
  EditablePdfProps,
  EditablePdfField,
  PdfPlaceholder
} from "@sharedComponents/EditablePdf/types";
import {useTranslation} from "react-i18next";
import Alert from "@sharedComponents/Alert";
import LoadingIndicator from "@sharedComponents/LoadingIndicator";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";
import Modal from "@sharedComponents/Modal";
import CancelModalContent from "./CancelModalContent";
import GeneratePdf from "./GeneratePdf";
import {IFileResponse, StatusCode} from "@apps/Shared/Services/Response.types";
import FlyersService from "@apps/Shared/Services/Flyers";
import {base64ToBlob, PdfMimeType} from "@apps/Shared/Utils/Files";
import {saveAs} from "file-saver";
import "./Scss/editable-pdf.scss";

function confirmCancel(
  setShowCloseModal: (value: boolean) => void,
  setHasDownloaded: (value: boolean) => void,
  setErrors: (errors: string[]) => void,
  onClose: () => void
) {
  setShowCloseModal(false);
  setHasDownloaded(false);
  setErrors([]);
  onClose();
}

function clickCancel(
  hasDownloaded: boolean,
  setShowCloseModal: (value: boolean) => void,
  setHasDownloaded: (value: boolean) => void,
  setErrors: (errors: string[]) => void,
  onClose: () => void
) {
  if (hasDownloaded) {
    return confirmCancel(setShowCloseModal, setHasDownloaded, setErrors, onClose);
  }
  setShowCloseModal(true);
}

function clickHideCancelModal(
  setShowCloseModal: (value: boolean) => void
) {
  setShowCloseModal(false);
}

function download(
  fileUrl: string,
  resolvedFields: EditablePdfField[],
  setDownloading: (value: boolean) => void,
  setHasDownloaded: (value: boolean) => void,
  setErrors: (errors: string[]) => void,
  formValues: Record<string, string>,
  generateErrorMessage: string
) {
  setErrors([]);
  setDownloading(true);
  setHasDownloaded(false);

  const body = {
    fileUrl,
    fields: resolvedFields.map(f => ({
      textInPdfToReplace: f.TextInPdfToReplace,
      value: formValues[f.TextInPdfToReplace],
      fontSize: f.FontSize ?? 36,
      leftAligned: f.LeftAligned ?? false
    }))
  };

  let pdf: IFileResponse = {fileName: "", file: []};

  FlyersService.GeneratePdf(body)
    .then((result: { status: StatusCode; data: IFileResponse & { message?: string[] } }) => {
      if (result.status === StatusCode.OK) {
        pdf = result.data;

        const file = base64ToBlob(pdf.file, PdfMimeType);
        saveAs(file, pdf.fileName);

        setHasDownloaded(true);
      } else {
        setErrors(result.data?.message || []);
      }
      setDownloading(false);
    })
    .catch(err => {
      console.error(err);
      setErrors([generateErrorMessage]);
      setDownloading(false);
    });
}

export const EditablePdf: React.FC<EditablePdfProps> = (props) => {
  const {t} = useTranslation();

  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [resolvedFields, setResolvedFields] = useState<EditablePdfField[]>(props.fields);
  const [pageWidth, setPageWidth] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);

    FlyersService.AnalyzePdf(props.fileUrl)
      .then((res: { data: PdfPlaceholder[] }) => {
        const placeholders = res.data;

        if (!placeholders?.length) {
          setErrors([t('error.noPlaceholdersFound')]);
          setLoading(false);
          return;
        }       

        const pageCount = new Set(placeholders.map(p => p.pageNumber)).size;

        if (pageCount !== 1) {
          setErrors([t('error.maxPagesExceeded')]);
          setLoading(false);
          return;
        }

        setPageWidth(placeholders[0].pageWidth);

        function clean(text: string) {
          return text
            .replace(/[\[\](){}<>]/g, "")
            .trim()
            .toLowerCase();
        }

        const matchedPlaceholders = placeholders.filter(ph =>
          props.fields.some(f => clean(f.TextInPdfToReplace) === clean(ph.text))
        );

        if (!matchedPlaceholders.length) {
          setErrors([t('error.noMatchingPlaceholders')]);
          setLoading(false);
          return;
        }

        setPageWidth(matchedPlaceholders[0].pageWidth);

        const resolved = matchedPlaceholders.map((ph) => {
          const original = props.fields.find(f =>
            clean(f.TextInPdfToReplace) === clean(ph.text)
          );

          return {
            TextInPdfToReplace: ph.text,
            FieldName: original?.FieldName,
            Label: original?.FieldName || ph.text,
            X: (ph.x / ph.pageWidth) * 100,
            Y: (1 - ph.y / ph.pageHeight) * 100,
            Width: (ph.width / ph.pageWidth) * 100,
            Height: (ph.height / ph.pageHeight) * 100,
            FontSize: original?.FontSize ?? 14,
            LeftAligned: original?.LeftAligned ?? false
          } as EditablePdfField;
        });

        setResolvedFields(resolved);
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        setErrors(['error.analyzeError']);
        setLoading(false);
      });
  }, [props.fileUrl]);

  const handleDownload = (values: Record<string, string>) => {
    download(
      props.fileUrl,
      resolvedFields,
      setDownloading,
      setHasDownloaded,
      setErrors,
      values,
      t("error.generatePdf")
    );
  };

  const errorsAlert =
    errors.length > 0 ? (
      <Alert type="danger" text="" subtext={errors} compact/>
    ) : null;

  return (
    <div className="edit-pdf">
      <Fullscreen open={true}>
        {loading ? (
          <LoadingIndicator customMargins="5rem 0"/>
        ) : (
          <>
            <div className="edit-pdf__form">
              <GeneratePdf
                heading={props.heading}
                fileUrl={props.fileUrl}
                previewUrl={props.previewUrl}
                previewImageAlt={props.previewImageAlt}
                fields={resolvedFields}
                loading={downloading}
                onDownload={handleDownload}
                pageWidth={pageWidth ?? undefined}
              >
                {errorsAlert}
              </GeneratePdf>
            </div>

            <Button
              text={
                <>
                  <Icon iconName="close"/> {t("button.close")}
                </>
              }
              type={ButtonType.SECONDARY}
              onClick={clickCancel.bind(
                null,
                hasDownloaded,
                setShowCancelModal,
                setHasDownloaded,
                setErrors,
                props.onClose
              )}
              disabled={downloading}
              className={'edit-pdf__cancel-button'}
            />

            <Modal
              open={showCancelModal}
              size="xs"
              handleClose={clickHideCancelModal.bind(null, setShowCancelModal)}
            >
              <CancelModalContent
                onConfirmCancel={confirmCancel.bind(
                  null,
                  setShowCancelModal,
                  setHasDownloaded,
                  setErrors,
                  props.onClose
                )}
                onHideCancelModal={clickHideCancelModal.bind(null, setShowCancelModal)}
              />
            </Modal>
          </>
        )}
      </Fullscreen>
    </div>
  );
};

export default EditablePdf;